import React, { useState } from "react";
import { toast } from "sonner";
import { X, Eye, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export const ReportReview = ({ request, catalog, onClose, onDecided }) => {
  const { t, lang } = useI18n();
  const L = (o) => (lang === "pt" ? o?.label_pt : o?.label_en) || "";
  const ent = (key) => catalog.entities.find((e) => e.key === key);
  const fieldDef = (ekey, fkey) => ent(ekey)?.fields.find((f) => f.key === fkey);

  // Translate a raw filter value into a human-readable label using the field's
  // catalog options (avoids showing raw UUIDs for department, role, etc.).
  const valueLabel = (fd, raw) => {
    if (!fd || !Array.isArray(fd.options)) return String(raw);
    const hit = fd.options.find((o) => String(o.value) === String(raw));
    return hit ? (lang === "pt" ? hit.label_pt : hit.label_en) : String(raw);
  };

  const [included, setIncluded] = useState(() => request.datasets.map((d) => new Set(d.fields)));
  const [comment, setComment] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveTpl, setSaveTpl] = useState(false);
  const [tplName, setTplName] = useState(request.title || "");
  const [tplDesc, setTplDesc] = useState("");

  const toggle = (idx, fkey) => {
    setIncluded((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const next = new Set(s);
      if (next.has(fkey)) next.delete(fkey); else next.add(fkey);
      return next;
    }));
    setPreview(null);
  };

  const finalDatasets = () => request.datasets
    .map((d, i) => ({ ...d, fields: d.fields.filter((f) => included[i].has(f)) }))
    .filter((d) => d.fields.length > 0);

  const anyFields = finalDatasets().length > 0;
  const totalRemoved = request.datasets.reduce((acc, d, i) => acc + d.fields.filter((f) => !included[i].has(f)).length, 0);

  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/requests/${request.id}/preview`, { datasets: finalDatasets() });
      setPreview(data.preview);
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
    finally { setBusy(false); }
  };

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/reports/requests/${request.id}/decide`, {
        decision, comment,
        datasets: decision === "approved" ? finalDatasets() : null,
        save_as_template: decision === "approved" && saveTpl,
        template_name: saveTpl ? (tplName || request.title) : null,
        template_description: saveTpl ? tplDesc : null,
      });
      toast.success(decision === "approved" ? t("reports.status_approved") : t("reports.status_rejected"));
      onDecided();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
    finally { setBusy(false); }
  };

  const filterDesc = (d) => (d.filters || []).map((f) => {
    const fd = fieldDef(d.entity, f.field);
    if (f.op === "in") return `${L(fd)}: ${(f.values || []).map((v) => valueLabel(fd, v)).join(", ")}`;
    if (f.op === "date_window") return `${L(fd)}: ${t(`reports.window_${f.window}`)}`;
    return `${L(fd)}: ${f.from || "…"} → ${f.to || "…"}`;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start lg:items-center justify-center p-4 overflow-y-auto" data-testid="report-review-modal">
      <div className="bg-white rounded-[20px] w-full max-w-3xl my-6 flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] bg-gradient-to-r from-[#1e293b] to-[#475569] rounded-t-[20px]">
          <div>
            <h2 className="text-white font-semibold text-[17px]">{request.title}</h2>
            <p className="text-[#cbd5e1] text-[12px] mt-0.5">{t("reports.requester")}: {request.requester_name} · {t(`reports.freq_${request.schedule?.frequency || "once"}`)}</p>
          </div>
          <button onClick={onClose} data-testid="review-close" className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {request.justification && (
            <div className="bg-[#f8fafc] rounded-[12px] p-3 text-[13px] text-[#475569]">
              <span className="font-semibold">{t("reports.justification")}:</span> {request.justification}
            </div>
          )}
          {request.has_confidential && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-[12px] p-3 text-[13px] font-medium text-rose-700" data-testid="review-confidential-banner">
              <ShieldAlert size={16} /> {t("reports.confidential_warning")}
            </div>
          )}

          {request.datasets.map((d, di) => (
            <div key={di} className="border border-[#e2e8f0] rounded-[14px] p-4">
              <div className="font-semibold text-[14px] text-[#1e293b] mb-1">{L(ent(d.entity))} <span className="text-[12px] font-normal text-[#94a3b8]">· {d.mode === "summary" ? t("reports.mode_summary") : t("reports.mode_detail")}</span></div>
              {filterDesc(d).length > 0 && (
                <div className="text-[12px] text-[#747474] mb-2">{filterDesc(d).join(" · ")}</div>
              )}
              <div className="flex flex-wrap gap-2">
                {d.fields.map((fk) => {
                  const fd = fieldDef(d.entity, fk);
                  const on = included[di]?.has(fk);
                  const conf = fd?.sensitivity === "confidential";
                  const restr = fd?.sensitivity === "restricted";
                  return (
                    <button key={fk} data-testid={`review-field-${di}-${fk}`} onClick={() => toggle(di, fk)}
                      className={`text-[12px] rounded-full px-3 py-1.5 border transition-all ${on
                        ? conf ? "bg-rose-600 text-white border-rose-600" : restr ? "bg-amber-500 text-white border-amber-500" : "bg-[#475569] text-white border-[#475569]"
                        : "border-[#e2e8f0] text-[#94a3b8] line-through"}`}>
                      {L(fd) || fk}{conf && " 🔴"}{restr && !conf && " 🟡"}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {totalRemoved > 0 && (
            <p className="text-[13px] text-amber-600" data-testid="review-removed-count">{t("reports.removed_fields_note")}: {totalRemoved}</p>
          )}

          <button data-testid="review-preview" onClick={doPreview} disabled={busy || !anyFields} className="sw-btn-secondary flex items-center gap-2 text-[13px]">
            <Eye size={15} /> {t("reports.preview")}
          </button>

          {preview && preview.map((p) => (
            <div key={p.entity} className="overflow-x-auto border border-[#e2e8f0] rounded-[12px]" data-testid={`review-preview-${p.entity}`}>
              <div className="px-3 py-2 text-[12px] font-semibold text-[#475569] bg-[#f8fafc] flex items-center justify-between">
                <span>{p.label}</span>
                <span className="text-[11px] font-medium text-[#94a3b8]">
                  {p.total_row_count === 0
                    ? `0 ${t("reports.rows")}`
                    : p.total_row_count > p.row_count
                      ? t("reports.showing_of_total").replace("{n}", p.row_count).replace("{total}", p.total_row_count)
                      : `${p.total_row_count} ${t("reports.rows")}`}
                </span>
              </div>
              {p.total_row_count === 0 ? (
                <div className="px-3 py-4 text-[12px] text-amber-700 bg-amber-50" data-testid={`review-empty-${p.entity}`}>
                  {t("reports.preview_empty_hint")}
                </div>
              ) : (
                <table className="w-full text-[12px]">
                  <thead><tr className="text-left text-[#94a3b8] border-b border-[#e2e8f0]">{p.columns.map((c, i) => <th key={i} className="px-3 py-1.5 whitespace-nowrap">{c}</th>)}</tr></thead>
                  <tbody>
                    {p.rows.map((row, i) => (
                      <tr key={i} className="border-b border-[#f1f5f9]">{row.map((v, j) => <td key={j} className="px-3 py-1.5 whitespace-nowrap text-[#475569]">{String(v ?? "")}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}

          <div>
            <label className="sw-label">{t("reports.comment")}</label>
            <textarea data-testid="review-comment" className="sw-input min-h-[60px]" value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>

          <div className="border border-[#e2e8f0] rounded-[14px] p-4 bg-[#f8fafc]">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="review-save-template-toggle">
              <input type="checkbox" checked={saveTpl} onChange={(e) => setSaveTpl(e.target.checked)} className="mt-1 accent-[#475569]" />
              <div>
                <div className="text-[13px] font-semibold text-[#1e293b]">{t("reports.save_as_template")}</div>
                <div className="text-[12px] text-[#747474]">{t("reports.save_as_template_hint")}</div>
              </div>
            </label>
            {saveTpl && (
              <div className="mt-3 space-y-2 pl-6">
                <div>
                  <label className="sw-label">{t("reports.template_name")}</label>
                  <input data-testid="review-template-name" className="sw-input" value={tplName} onChange={(e) => setTplName(e.target.value)} maxLength={120} />
                </div>
                <div>
                  <label className="sw-label">{t("reports.template_description")}</label>
                  <textarea data-testid="review-template-description" className="sw-input min-h-[50px]" value={tplDesc} onChange={(e) => setTplDesc(e.target.value)} maxLength={400} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0]">
          <button data-testid="review-reject" onClick={() => decide("rejected")} disabled={busy} className="rounded-full px-5 py-2.5 text-[13px] font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors">{t("reports.reject")}</button>
          <button data-testid="review-approve" onClick={() => decide("approved")} disabled={busy || !anyFields} className="sw-btn-primary">{t("reports.approve")}</button>
        </div>
      </div>
    </div>
  );
};

export default ReportReview;
